// ...existing code...
/**
 * Generates initials from a name string
 * @param name - The name to extract initials from
 * @param maxInitials - Maximum number of initials to return (default: 2)
 * @returns A string of uppercase initials
 */
export function getInitials(name?: string | null, maxInitials: number = 2): string {
  // Handle null, undefined, or empty strings
  if (!name || !name.trim()) return '?'

  // Split by whitespace and filter out empty strings
  const words = name.trim().split(/\s+/).filter(word => !!word)

  if (words.length === 0) return '?'

  const safeChar = (w?: string, idx = 0) => (w?.charAt(idx) || '')

  if (words.length === 1) {
    // For single word, take first character or first two if maxInitials allows
    const word = words[0] ?? ''
    if (maxInitials <= 1) {
      const c = safeChar(word, 0)
      return c ? c.toUpperCase() : '?'
    }
    const slice = word.slice(0, Math.max(1, Math.min(maxInitials, word.length)))
    return slice ? slice.toUpperCase() : '?'
  }

  // For multiple words, take first and last (or customize based on maxInitials)
  if (maxInitials <= 1) {
    const c = safeChar(words[0], 0)
    return c ? c.toUpperCase() : '?'
  }

  if (maxInitials === 2) {
    const first = safeChar(words[0], 0)
    const last = safeChar(words[words.length - 1], 0)
    const combined = (first + last).trim()
    return combined ? combined.toUpperCase() : '?'
  }

  // For maxInitials > 2, take first N words
  const initials = words
    .slice(0, maxInitials)
    .map(word => safeChar(word, 0))
    .join('')
    .trim()

  return initials ? initials.toUpperCase() : '?'
}
// ...existing code...